/**
 * Pure payload builder for the email-campaign audience builder.
 *
 * `FacetState` is the "select all, then uncheck" model: selecting a
 * relationship or contact group means ALL its people are IN — the operator
 * unchecks individuals as exceptions (their lower-cased email lands in
 * `excluded`). `industries` narrows the resolved population (a filter, not a
 * source). `individuals` are people hand-added via search/manual-email, on
 * top of whatever the relationship/group/industry sources resolve to.
 *
 * `buildTargetSelection` folds that UI state into the minimal,
 * backward-compatible `TargetSelection` payload the rest of the app
 * (crm-api, EmailCampaign#recipients_from_target_selection) already
 * understands — see `email-target-selector.tsx`.
 *
 * "Minimal" matters here: older payloads never had a `filters` key at all,
 * and crm-api / other readers may treat `filters` presence as "a filter is
 * active". So this never emits `filters: {}` and never emits empty
 * `industries`/`relationship`/`exclude` arrays or an empty `groups` array.
 */

import type { RelationshipFacet, AudienceFilters } from "./facets"
import type { EmailRecipient, TargetSelection } from "../email-target-selector"

export interface FacetState {
  /** Selected relationship populations — ALL their people are in. */
  relationships: RelationshipFacet[]
  /** Selected contact-list (group) ids — ALL their members are in. */
  groups: number[]
  /** Canonical vertical labels narrowing the resolved population. */
  industries: string[]
  /** Lower-cased emails the operator unchecked out of an otherwise-included
   * source population. */
  excluded: string[]
  /** Explicitly hand-added people (search / add-manual-email). */
  individuals: EmailRecipient[]
}

export function buildTargetSelection(state: FacetState): TargetSelection {
  const filters: AudienceFilters = {}

  if (state.relationships.length > 0) {
    filters.relationship = state.relationships
  }
  if (state.industries.length > 0) {
    filters.industries = state.industries
  }
  if (state.excluded.length > 0) {
    filters.exclude = state.excluded
  }

  const hasFilters = Object.keys(filters).length > 0

  return {
    recipients: state.individuals,
    ...(state.groups.length > 0 ? { groups: state.groups } : {}),
    ...(hasFilters ? { filters } : {}),
  }
}
